import initPersistor from './stores/persistor';
import { Logger } from './types';

enum DebugLevelEnum {
  'debug' = 1,
  'info' = 2,
  'warn' = 3,
  'none' = 4,
}

type DebugLevel = keyof typeof DebugLevelEnum;

const context = {
  debugLevelPersistor: initPersistor<number>({ ttlSeconds: 365 * 24 * 60 * 60, storageKey: 'debugger' }),
  loggers: [] as Logger[],
};

type Debugger = Record<Exclude<DebugLevel, 'none'>, (message: string, ...data: unknown[]) => void>;

export const addLogger = (logger: Logger) => {
  context.loggers.push(logger);
};

export const setBaseLevel = (baseLevel: DebugLevel) => {
  const knownLevel = context.debugLevelPersistor.get(false);
  if (!knownLevel) context.debugLevelPersistor.set(DebugLevelEnum[baseLevel]);
};

const initDebugger = (): Debugger => {
  const log =
    (level: Exclude<DebugLevel, 'none'>) =>
    (message: string, ...data: unknown[]) => {
      const debugLevel = context.debugLevelPersistor.get(false) || 4;
      if (DebugLevelEnum[level] >= debugLevel) {
        console[level](message, ...data);
        if (level === 'warn') context.loggers.forEach(logger => logger.warn(message, data[0]));
      }
    };

  return {
    debug: log('debug'),
    info: log('info'),
    warn: log('warn'),
  };
};

type MdatDebugType = DebugLevel | boolean | 'verbose';

declare global {
  interface Window {
    /**
     * Set the debug level of the data analyticstracker module
     */
    mdatDebug: MdatDebugType;
  }
}

const defaultLevel = 'info';

const toDebugLevel = (level: MdatDebugType) => {
  if (typeof level === 'boolean') {
    return level ? DebugLevelEnum[defaultLevel] : DebugLevelEnum['none'];
  }

  if (level === 'verbose') return DebugLevelEnum['debug'];

  return DebugLevelEnum[level] || DebugLevelEnum[defaultLevel];
};

Object.defineProperty(window, 'mdatDebug', {
  get: function () {
    return DebugLevelEnum[context.debugLevelPersistor.get(false) || DebugLevelEnum['none']];
  },
  set: function (level: MdatDebugType) {
    const logLevel = toDebugLevel(level);

    context.debugLevelPersistor.set(logLevel);
    console.log(
      logLevel === DebugLevelEnum['none']
        ? 'mdat debugging has been disabled'
        : `mdat debug level has been set to ${DebugLevelEnum[logLevel]}`,
    );
  },
});

export default initDebugger();
